<template>
    <loading-spinner v-if="!nft || isLoading" />
    <div v-else>
        <h2 class="title">{{ $t('nft.withdrawal.summaryTitle') }}</h2>
        <wallet-form v-if="Object.keys(recipient).length && !isLoading"
            class="confirmation-recipient"
            is-withdraw-page
            :isButtonShow="!isPasswordMethod"
            :isCancelButtonShow="false"
            :isButtonDisabled="isConfirmDisabled"
            :buttonText="$t('global.button.proceed')"
            @proceedButtonAction="onSendClick"
            @proceedCancelButtonAction="goBack()"
        >
            <div class="flex">
                <img :src="nft.imageUrl"
                    class="top-row__illustration"
                >
                <div class="flex flex--column flex--justify-center top-row">
                    <div class="flex flex--row flex--align-center top-row__partner-infos">
                        <partner-logo class="top-row__partner"
                            :show-placeholder="true"
                            :url="nft.logo"
                        />
                        <div class="flex flex--column">
                            <div class="top-row__title">{{ nft.title }}</div>
                            <div class="top-row__subtitle">{{ nft.subtitle }}</div>
                            <div class="top-row__quantity top-row__subtitle">{{ $t('nft.withdrawal.quantity') }}</div>
                            <div class="top-row__title">1</div>
                        </div>
                    </div>
                </div>
            </div>
            <recipient-card id="withdraw-confirmation-recipient"
                :recipient="recipient"
                is-disabled
                is-solo
            />
            <label v-if="isPasswordMethod"
                class="confirmation-authentication__title"
            >{{ $t('auth.authTitle') }}</label>
            <label v-if="isPasswordMethod"
                class="confirmation-authentication__description"
            >{{ $t('auth.authNFTDescription') }}</label>
            <label v-if="isPasswordMethod"
                class="label confirmation confirmation-authentication__password"
            >
                {{ $t('auth.yourPassword') }}:

                <input id="input-password"
                    v-model="password"
                    type="password"
                    :placeholder="$t('auth.enterPassword')"
                    class="label__input"
                >
            </label>
            <div v-if="isPasswordMethod"
                class="confirmation-authentication__buttons"
            >
                <button id="button-confirm"
                    :class="['button', { 'button--disabled': isConfirmDisabled }]"
                    @click="onSendClick"
                >
                    {{ $t('auth.confirmAndSend') }}
                </button>
                <button id="button-cancel"
                    class="button button-cancel"
                    @click="goBack()"
                >
                    {{ $t('global.button.cancel') }}
                </button>
            </div>
        </wallet-form>
    </div>
</template>

<script>
import {
    mapActions,
    mapGetters,
} from 'vuex';

// Local Modules
import localEpicTypes from '@webapp/epics/types.js';
import localStoreTypes from '@webapp/store/types.js';

// Components
import loadingSpinner from '@webapp/components/loading-spinner/loading-spinner.vue';
import walletForm from '@webapp/components/wallet/wallet-form.vue';
import recipientCard from '@webapp/components/wallet/recipient-card.vue';
import partnerLogo from '@webapp/components/partner-logo/partner-logo.vue';

export default {
    name: 'summary',
    components: {
        walletForm,
        recipientCard,
        partnerLogo,
        loadingSpinner,
    },
    props: {
        recipient: {
            type: Object,
            default: () => ({}),
        },
        nft: {
            type: Object,
            default: () => ({}),
        },
        collectibleId: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            method: null,
            password: '',
            isLoading: true,
            isSendingNft: false,
        };
    },
    computed: {
        ...mapGetters([
            'userAuthMethod',
            'validateAuthResponse',
            'getLatestSimpleValidationError',
        ]),

        isPasswordMethod() {
            return this.method === 'password';
        },

        isConfirmDisabled() {
            if (this.isLoading || this.isSendingNft) {
                return true;
            }

            return (this.isPasswordMethod && this.password.trim() === '');
        },
    },
    watch: {
        validateAuthResponse: 'proceedToWithdraw',
        userAuthMethod(auth) {
            // 2FA must be enabled to set auth method
            this.method = auth.status === 'enabled' && auth.isEnabledFor2FA && auth.authenticationMethod ? auth.authenticationMethod.method : '';
            this.isLoading = false;
        },
        getLatestSimpleValidationError: 'onLatestError',
    },
    async mounted() {
        await this.getUserAuthMethod();
    },
    methods: {
        ...mapActions('nfts', [
            'transferNFT',
            'fetchRecipients',
        ]),

        ...mapActions({
            getUserAuthMethod: localEpicTypes.GET_USER_AUTH_METHOD,
            validateAuth: localEpicTypes.VALIDATE_USER_AUTH,
            setModal: localStoreTypes.SET_MODAL,
        }),

        async proceedToWithdraw() {
            await this.transferNFT({
                collectibleId: this.collectibleId,
                toExternalAddressId: this.recipient.id,
            });
            await this.fetchRecipients({
                networkTypes: [this.nft.networkType],
            });
            this.isSendingNft = false;
            this.openProcessingPage();
        },

        onSendClick() {
            if (this.isConfirmDisabled) {
                return false;
            }

            this.isSendingNft = true;
            if (this.method) {
                // Confirm auth
                this.validateAuth({
                    event: 'nft_withdrawal',
                    method: this.method,
                    oldPassword: this.password,
                    newPassword: '',
                });
            } else {
                this.proceedToWithdraw();
            }
        },

        goBack() {
            this.$router.go(-1);
        },

        onLatestError() {
            // Display error is handled in the sendout.vue
            this.isSendingNft = false;
        },

        openProcessingPage() {
            this.setModal({
                isVisible: true,
                isClosable: false,
                componentToLoad: 'components/sendout/confirmation-modal',
                data: {
                    callback: () => this.$router.push('/digital-collectibles'),
                    label: this.$t('nft.listTitle'),
                },
            });
        },
    },
};
</script>


<style lang="scss" scoped>
.title {
    padding: 3rem 2rem 0 2rem;
    color: $cyprus-color;
    font-size: 2rem;
    font-weight: bold;
}

.top-row {
    text-align: left;

    &__partner {
        object-fit: contain;
        margin: 0 1.5rem 0 0;
        width: 4rem;
        height: 4rem;
        align-self: flex-start;

        &-infos {
            margin-bottom: 3rem;
            flex-shrink: 1;
        }
    }

    &__title {
        font-size: 18px;
        font-weight: bold;
    }

    &__subtitle {
        font-weight: 600;
        font-size: 13px;
    }

    &__illustration {
        object-fit: contain;
        border-radius: 0.65rem;
        margin: 1rem 2rem 1rem 0;
        max-height: 10rem;
        max-width: 100%;
    }

    &__quantity {
        margin-top: 3.5rem;
    }
}

.confirmation {
    margin-top: 3rem;

    &-authentication {
        flex-direction: column;

        &__password {
            margin-bottom: 6rem;
            text-align: left;
        }

        &__title {
            margin-top: 5rem;
            font: 700 1.8rem/2.7rem "Nunito";
            padding-bottom: 1rem;
            color: $cyprus-color;
        }

        &__description {
            font-size: 1.5rem;
            line-height: 2rem;
            margin-bottom: 1rem;
        }

        &__buttons {
            display: flex;
            flex-direction: column;

            .button {
                background-color: $blue-ribbon-color;
                width: 34rem;
                max-width: 100%;
                margin: 0 auto;
                padding: 1.1rem;
                font-size: 1.5rem;

                &--disabled {
                    opacity: 0.5;
                    pointer-events: none;
                }

                &-cancel {
                    margin-top: 2rem;
                    background: none;
                    color: inherit;
                }
            }
        }
    }
}
</style>